import { BASE_DOC_URL } from '../constants';
import innerEmitter from '../../inner-emitter/inner-emitter';
import INNER_EVENT from '../../inner-emitter/inner-event';
import eventLogger from '../../utils/log/event-log';
import loggerManager from '../../utils/log/logger-manager';
import { isLangChinese } from '../../utils/utils';

const CLASS_PREFIX = 'trtc_autoplay';
const CLASS_MASK = `${CLASS_PREFIX}_mask`;
const CLASS_WRAPPER = `${CLASS_PREFIX}_wrapper`;
const CLASS_HEADER = `${CLASS_PREFIX}_header`;
const CLASS_CONTENT = `${CLASS_PREFIX}_content`;
const CLASS_ACTION_WRAPPER = `${CLASS_PREFIX}_action_wrapper`;
const CLASS_ACTION_QUESTION = `${CLASS_PREFIX}_question`;
const CLASS_ACTION_COLLAPSE = `${CLASS_PREFIX}_collapse`;
const CLASS_ACTION_CONFIRM = `${CLASS_PREFIX}_action_confirm`;
const CLASS_DETAIL = `${CLASS_PREFIX}_detail`;
const COLOR_BLUE = '#2473E8';

const DIALOG = 'dialog';
const STAT_SHOW_DIALOG = `${DIALOG}-show`;
const STAT_CLICK_DETAIL = `${DIALOG}-1`;
const STAT_CLICK_QUESTION = `${DIALOG}-2`;

let isStyleAdded = false;
let isShowing = false;
export const isAutoPlayDialogShowing = () => isShowing;

const DOC_LINK = `${BASE_DOC_URL}/${
  isLangChinese() ? 'zh-cn' : 'en'
}/tutorial-21-advanced-auto-play-policy.html`;

const OTHER_SOLUTION_LINK = `<br><a href='${DOC_LINK}' target='_blank'>${
  isLangChinese() ? '其他方案？' : 'Any other solution?'
}</a>`;
const DETAIL_TEXT = `${
  isLangChinese()
    ? `浏览器自动播放策略：在用户与页面产生交互（点击、触摸）之前，浏览器禁止播放有声媒体。该弹窗用于帮助用户恢复音视频播放。${OTHER_SOLUTION_LINK}`
    : `Autoplay Policy: Before user interacts with the web page (clicking, touching), page will not be allowed to play media with sound. This Dialog is used to help users resume playback. ${OTHER_SOLUTION_LINK}`
}`;

class AutoPlayDialog {
  content = '音视频播放被浏览器拦截，请点击“恢复播放”。';
  private _dialogNode: HTMLElement | null = null;
  private _bodyPosition = '';
  private _showDetail = false;
  private _isCollapseClicked = false;
  private _isQuestionClicked = false;
  constructor() {
    if (!isLangChinese()) {
      this.content = 'Media playback failed. Click the "Resume" to resume playback.';
    }

    // 加载样式
    if (!isStyleAdded) {
      const style = document.createElement('style');
      style.innerHTML = `.${CLASS_MASK}{position:fixed;top:0;left:0;right:0;bottom:0;width:100vw;height:100vh;display:flex;justify-content:center;align-items:center;background:rgba(0,0,0,0.5);z-index:1500;}.${CLASS_MASK} div:not(.${CLASS_ACTION_WRAPPER}){display:block !important;}.${CLASS_WRAPPER}{padding:14px;background:#fff;border-radius:3px;box-shadow:0px 3px 15px #434343;border:1px solid #d1cfcf;max-width:500px;}.${CLASS_WRAPPER} a{color:${COLOR_BLUE};}.${CLASS_HEADER}{overflow:hidden;text-overflow:ellipsis;font-size:16px;font-weight:600;}.${CLASS_CONTENT}{margin:8px 0;}.${CLASS_ACTION_WRAPPER}{width:100%;display:flex !important;align-items:center;justify-content:right;float:right;}.${CLASS_ACTION_COLLAPSE}{margin-right:auto;cursor:pointer}.${CLASS_ACTION_QUESTION}{height:100%;line-height:16px;cursor:pointer;}.${CLASS_ACTION_CONFIRM}{margin-left:8px;color:#fff;background:${COLOR_BLUE};padding:4px 12px;outline:none;border:1px solid;border-radius:3px;font-weight:bold;}.${CLASS_ACTION_CONFIRM}:hover{opacity:0.9;}.${CLASS_ACTION_COLLAPSE},.${CLASS_ACTION_CONFIRM},.${CLASS_CONTENT},.${CLASS_ACTION_QUESTION}{font-size:14px;}@media screen and (max-width:750px){.${CLASS_WRAPPER}{width:80vw;}}`;
      document.head.appendChild(style);
      isStyleAdded = true;
    }

    this.addDiaLog();
  }

  createDiaLog() {
    const template = document.createElement('template');
    template.innerHTML = `<div class="${CLASS_MASK}"><div class='${CLASS_WRAPPER}'><div class='${CLASS_HEADER}'>${location.host}</div><div class='${CLASS_CONTENT}'>${this.content}</div><div class='${CLASS_DETAIL}' style="visibility:hidden;width:100%;height:0;font-size:12px;color:gray;">${DETAIL_TEXT}</div><div class='${CLASS_ACTION_WRAPPER}'></div></div></div>`.trim();

    const confirmButton = document.createElement('button');
    confirmButton.className = CLASS_ACTION_CONFIRM;
    confirmButton.innerText = isLangChinese() ? '恢复播放' : 'Resume';
    confirmButton.onclick = this.onConfirm.bind(this);

    const questionButton = document.createElement('div');
    questionButton.className = CLASS_ACTION_QUESTION;
    questionButton.innerHTML = `<?xml version="1.0" encoding="UTF-8"?>
    <svg class="icon" width="18" height="18" p-id="2030" t="1639646523624" version="1.1" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
    <path d="m464 784.35c0 26.51 21.49 48 48 48s48-21.49 48-48-21.49-48-48-48-48 21.49-48 48z" p-id="2031"/>
    <path d="m512 960c-247.04 0-448-200.96-448-448s200.96-448 448-448 448 200.96 448 448-200.96 448-448 448zm0-831.71c-211.58 0-383.71 172.13-383.71 383.71 0 211.55 172.13 383.71 383.71 383.71 211.55 0 383.71-172.16 383.71-383.71 0-211.58-172.16-383.71-383.71-383.71z" p-id="2032"/>
    <path d="m512 673.7c-17.665 0-32.001-14.336-32.001-31.999v-54.112c0-52.353 40-92.352 75.328-127.65 25.887-25.92 52.672-52.672 52.672-74.017 0-53.343-43.072-96.735-95.999-96.735-53.823 0-95.999 41.536-95.999 94.559 0 17.665-14.336 31.999-32.001 31.999s-32.001-14.336-32.001-31.999c0-87.424 71.775-158.56 160-158.56s160 72.095 160 160.74c0 47.904-36.32 84.192-71.424 119.3-27.84 27.776-56.576 56.512-56.576 82.336v54.112c0 17.665-14.336 32.032-32.001 32.032z" p-id="2033"/>
    </svg>
    `;
    questionButton.onclick = this.onQuestionClick.bind(this);

    const collapseButton = document.createElement('div');
    collapseButton.className = CLASS_ACTION_COLLAPSE;
    collapseButton.innerText = `${isLangChinese() ? '详情 >' : 'Detail >'}`;
    collapseButton.onclick = this.onCollapseClick.bind(this);

    const node = template.content.firstChild as HTMLElement;
    const actionWrapper = node.querySelector(`.${CLASS_ACTION_WRAPPER}`);
    actionWrapper!.appendChild(collapseButton);
    actionWrapper!.appendChild(questionButton);
    actionWrapper!.appendChild(confirmButton);
    return node;
  }

  addDiaLog() {
    if (!isAutoPlayDialogShowing()) {
      isShowing = true;
      this._dialogNode = this.createDiaLog();
      document.body.appendChild(this._dialogNode);
      // 点击遮罩也可恢复播放
      this._dialogNode.onclick = this.onConfirm.bind(this);
      // 点击弹框区域时，阻止点击事件冒泡，避免遮罩触发点击事件。
      // @ts-ignore
      this._dialogNode.querySelector(`.${CLASS_WRAPPER}`)!.onclick = (e: Event) => e.stopPropagation();
      // 显示 dialog 时，让页面无法滚动。
      this._bodyPosition = document.body.style.position;
      document.body.style.position = 'fixed';
      loggerManager.info('show autoplay dialog');
      eventLogger.uploadEvent({ log: STAT_SHOW_DIALOG });
    }
  }

  deleteDialog() {
    if (this._dialogNode) {
      document.body.removeChild(this._dialogNode);
      // 关闭 dialog，恢复页面可以正常滚动
      document.body.style.position = this._bodyPosition;
      this._dialogNode = null;
      isShowing = false;
    }
    dialog = null;
  }

  /**
   * 用户点击了 Confirm，调用接口恢复音视频播放。
   * @memberof AutoPlayDialog
   */
  onConfirm() {
    loggerManager.warn('confirm clicked, try resume stream');
    innerEmitter.emit(INNER_EVENT.AUTOPLAY_DIALOG_CLICK_CONFIRM);
    this.deleteDialog();
  }

  onCollapseClick() {
    const detail = this._dialogNode!.querySelector(`.${CLASS_DETAIL}`) as HTMLElement;
    detail!.style!.visibility = `${this._showDetail ? 'hidden' : 'visible'}`;
    detail!.style!.height = `${this._showDetail ? 0 : 'fit-content'}`;
    this._showDetail = !this._showDetail;
    if (!this._isCollapseClicked) {
      eventLogger.uploadEvent({ log: STAT_CLICK_DETAIL });
    }
    this._isCollapseClicked = true;
  }

  onQuestionClick() {
    window.open(DOC_LINK, '_blank');
    if (!this._isQuestionClicked) {
      eventLogger.uploadEvent({ log: STAT_CLICK_QUESTION });
    }
    this._isQuestionClicked = true;
  }
}

let dialog: AutoPlayDialog | null = null;
export function showAutoPlayDialog() {
  if (!dialog) {
    dialog = new AutoPlayDialog();
  }
}
export function deleteAutoPlayDialog() {
  if (dialog) {
    dialog.deleteDialog();
  }
}

export default AutoPlayDialog;
